/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Quick Schedule step constants and stepper hook
 *
 * @file quickScheduleSteps.js
 * @author Anuja Gaikwad
 * @since 2.0.3
 */

import { useCallback, useState } from 'react';

import { EMPTY_QUICK_SCHEDULE_FORM_VALUES } from './quickScheduleFormUtils';

export const QUICK_SCHEDULE_STEP_IDS = {
  SCHEDULE: 'schedule',
  REVIEW: 'review'
};

export const QUICK_SCHEDULE_STEPS = [
  { id: QUICK_SCHEDULE_STEP_IDS.SCHEDULE, label: '1' },
  { id: QUICK_SCHEDULE_STEP_IDS.REVIEW, label: '2' }
];

export const isQuickScheduleStepReachable = (
  stepId,
  steps = QUICK_SCHEDULE_STEPS,
  completedStepIds = []
) => {
  const targetIndex = steps.findIndex((step) => step.id === stepId);

  if (targetIndex <= 0) {
    return true;
  }

  for (let index = 0; index < targetIndex; index += 1) {
    if (!completedStepIds.includes(steps[index].id)) {
      return false;
    }
  }

  return true;
};

export const markQuickScheduleStepCompleted = (completedStepIds = [], stepId) => (
  completedStepIds.includes(stepId)
    ? completedStepIds
    : [...completedStepIds, stepId]
);

export const getNextQuickScheduleStepId = (
  stepId,
  steps = QUICK_SCHEDULE_STEPS
) => {
  const currentIndex = steps.findIndex((step) => step.id === stepId);
  const nextStep = steps[currentIndex + 1];

  return nextStep?.id ?? stepId;
};

export const useQuickScheduleStepper = ({
  onFinish,
  steps = QUICK_SCHEDULE_STEPS
} = {}) => {
  const [activeStepId, setActiveStepId] = useState(QUICK_SCHEDULE_STEP_IDS.SCHEDULE);
  const [completedStepIds, setCompletedStepIds] = useState([]);
  const [formValues, setFormValues] = useState(EMPTY_QUICK_SCHEDULE_FORM_VALUES);

  const onStepChange = useCallback((stepId) => {
    if (stepId === activeStepId) {
      return;
    }

    if (!isQuickScheduleStepReachable(stepId, steps, completedStepIds)) {
      return;
    }

    setActiveStepId(stepId);
  }, [activeStepId, completedStepIds, steps]);

  const onStepComplete = useCallback((stepId, data) => {
    if (data && !data.skipped) {
      setFormValues(data);
    }

    setCompletedStepIds((previousIds) => (
      markQuickScheduleStepCompleted(previousIds, stepId)
    ));

    if (stepId === QUICK_SCHEDULE_STEP_IDS.REVIEW) {
      if (onFinish) {
        onFinish(data);
      }

      return;
    }

    setActiveStepId(getNextQuickScheduleStepId(stepId, steps));
  }, [onFinish, steps]);

  return {
    steps,
    activeStepId,
    completedStepIds,
    formValues,
    onStepChange,
    onStepComplete
  };
};
